import React, { useState, useRef, useContext, useReducer, useEffect } from "react";
import "../css/pdfTimesheet.scss";
import { hesLogo } from "../images/hesLogo";
import { useLocation } from "react-router-dom";
import { auth } from "../firebase";
import { dateTimeFormatter } from "../helperFunctions/dateFormatters";
import {
  // calcMaterialsVat,
  calcJobsMaterials,
  calcJobsLabour,
  calcJobsTotalCost,
  calcJobsSetPrices,
  calcJobsDiscounts,
  calcVat,
} from "../helperFunctions/calculationFunctions";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BuildIcon from "@mui/icons-material/Build";
import CloseIcon from "@mui/icons-material/Close";

import Footer from "./Footer";

import { tooltips, vat_number } from "../popData";
import IdentifierBar from "./IdentifierBar";
import { printSheet } from "../helperFunctions/printFunctions";
import { getDbRef } from "../helperFunctions/determineType";
import { arrayUnion, doc } from "firebase/firestore";
import { updatePrimary } from "../helperFunctions/primaryWrites";
import { AUTOMATION_PREFIX } from "../helperFunctions/pendingDraftsCheck";
import { SlidersContext } from "../providers/SlidersProvider";

import PdfEmailBox from "./PdfEmailBox";
import PdfToolbar from "./PdfToolbar";
import {
  SET_EMAIL_SENT_STATUS,
  SET_EMAIL_MESSAGE,
  TOGGLE_PDF_MENU,
  TOGGLE_EMAIL_MENU,
  pdfPageReducer,
  initialPdfState,
} from "../reducers/pdfReducer";
import {
  formatMessage,
  formatSubject,
  formatAddress,
  getMaterials,
  getSetPrices,
  calculateSetPricesLine,
  calculateMaterialsLine,
  formatAsFilename,
  removeCardFees,
} from "../helperFunctions/pdfFunctions";
import { setupTemplateVariables } from "../helperFunctions/templateHelpers";
import { SettingsContext } from "../providers/SettingsProvider";
import { TemplatesContext } from "../providers/TemplatesProvider";
import {
  getWorkflow,
  inferWorkflowKeyFromJobDetails,
  workflowKeyForCategory,
  categoryForWorkflowKey,
  CERT_ELIGIBLE_WORKFLOW_KEYS,
} from "../helperFunctions/workflows";

const PdfTimesheet = (props) => {
  const pdfPage = useRef(null);
  const location = useLocation();

  const { sliders, setSliderId } = useContext(SlidersContext);
  const { aiThreadMatchingEnabled } = useContext(SettingsContext);

  let { timesheet, jobDescriptions } = location.state;
  const vat = timesheet?.vatMode;

  // Thread picker (attach quote draft to an existing Gmail conversation) >>>>>>>>>>>>
  const [threadCandidates, setThreadCandidates] = useState([]);
  const [selectedThreadIndex, setSelectedThreadIndex] = useState(null); // null = standalone draft
  // Kept separate from selectedThreadIndex (which the user can change) so the UI can
  // still show which option the AI actually suggested, even after it's overridden.
  const [aiSuggestedIndex, setAiSuggestedIndex] = useState(null);
  const [aiSuggestedReason, setAiSuggestedReason] = useState(null);
  // Distinguishes "AI explicitly found no match" from "AI was never asked" (disabled in
  // Settings) - both otherwise collapse to aiSuggestedIndex === null.
  const [aiAttempted, setAiAttempted] = useState(false);
  // True only when the AI was asked but every provider failed - lets the UI tell that
  // apart from a genuine "looked and found nothing" (aiAttempted && !aiError).
  const [aiError, setAiError] = useState(false);
  // Thread picker <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Relevant to PDF state >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  // Initialisers ----------------------------------------------
  let initialPdfType = props.isQuote ? "Quote" : "Invoice";
  let initialShowReview = false;
  let { addressLine, emailAddressLine } = setupTemplateVariables(timesheet);

  let emailSentStatus = timesheet.dateSent ? `Sent on ${timesheet.dateSent}` : "---";

  // Prefers the Gmail Add-on's AI-classified jobTypeCategory (see emailFieldExtractor.js)
  // when this primary has one, over the plain keyword matcher below - the AI reads the whole
  // job description, not just a fixed EICR/PAT/Smoke Alarms vocabulary, so it's more reliable
  // for the varied phrasing real customer emails use. Falls back to keyword matching exactly
  // as before for any primary without a stored category (i.e. everything created the
  // traditional way, not through the Add-on).
  let initialWorkflowKey =
    workflowKeyForCategory(timesheet.jobTypeCategory, initialPdfType) ||
    inferWorkflowKeyFromJobDetails(timesheet.jobDetails, initialPdfType);
  let initialWorkflow = getWorkflow(initialWorkflowKey);

  // -----------------------------------------------------------

  const [pdfState, dispatch] = useReducer(pdfPageReducer, {
    ...initialPdfState,
    // showPdfMenu: true,
    // showEmailMenu: true,
    // Default the VAT breakdown open for VAT jobs - otherwise every VAT job starts with
    // it hidden (the toolbar's VAT button only even appears when `vat` is true, so leaving
    // the default at false meant staff had to click "Show" on every single VAT job).
    showVatBreakdown: Boolean(vat),
    pdfType: initialPdfType,
    // both: false,
    //showDiscounts: true,
    //showMaterials:  //props.isQuote,
    //showBreakdown:  //!props.isQuote,
    showReview: initialShowReview,
    emailSentStatus,
    timesheet,
    workflowKey: initialWorkflowKey,
    subjectPrefix: initialWorkflow.subjectPrefix,
    messageType: initialWorkflow.messageType,
    subject: formatSubject({
      pdfType: initialPdfType,
      subjectPrefix: initialWorkflow.subjectPrefix,
      timesheet,
    }),
    message: formatMessage({
      pdfType: initialPdfType,
      showReview: initialShowReview,
      addressLine: emailAddressLine,
      timesheet,
      workflowKey: initialWorkflowKey,
      messageType: initialWorkflow.messageType,
    }),
  });

  // The initial message above is built from getTemplatesSnapshot()'s cache, which is still
  // {} until TemplatesProvider's onSnapshot delivers its first batch - same lazy-init trap
  // as SettingsContext.vat elsewhere in this app. Once real template data streams in, redo
  // that computation and correct the message - but only if it hasn't been touched since
  // (compared against the last value *this* effect itself set, so it can never clobber a
  // manual edit or a later SELECT_WORKFLOW/TOGGLE_IS_PAID dispatch).
  const { templates } = useContext(TemplatesContext);
  let lastSyncedMessageRef = useRef(pdfState.message);
  useEffect(() => {
    if (pdfState.message !== lastSyncedMessageRef.current) return;

    let resynced = formatMessage({
      pdfType: pdfState.pdfType,
      showReview: pdfState.showReview,
      addressLine: emailAddressLine,
      timesheet: pdfState.timesheet,
      workflowKey: pdfState.workflowKey,
      messageType: pdfState.messageType,
    });

    if (resynced !== pdfState.message) {
      lastSyncedMessageRef.current = resynced;
      dispatch({ type: SET_EMAIL_MESSAGE, payload: resynced });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  let { refNumber, worksOrderNumber, address, address2, city, postcode, flat } = pdfState.timesheet;

  // Relevant to PDF state <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Pdf status state >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  // Pdf status state <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // const [isQuote, toggleIsQuote] = useState(props.isQuote ? true : false);

  //Sendbar///////////////////////////////////////
  const hesRef = `${pdfState.pdfType}_${refNumber || "no_ref"}`;

  ////////////////////////////////////////////////

  //Toolbar///////////////////////////////////

  // const [showJobDescriptions, toggleJobDescriptions] = useState(true);

  let dbRef = getDbRef(timesheet?.primaryType);

  ///////////////////////////////////////////////

  let jobReceived = dateTimeFormatter(timesheet.jobReceived, "d_full");
  let jobStart = dateTimeFormatter(timesheet.jobStart, "d_full");
  let jobEnd = dateTimeFormatter(timesheet.jobEnd, "d_full");

  let exportDate = dateTimeFormatter(new Date(), "d_full");

  // Remove card fees from pdf view
  jobDescriptions = removeCardFees(jobDescriptions);

  let [materialsCost] = calcJobsMaterials(jobDescriptions);
  let labourTotal = calcJobsLabour(jobDescriptions);
  let setPricesCost = calcJobsSetPrices(jobDescriptions, vat);
  let totalCost = calcJobsTotalCost(jobDescriptions, vat)[4];
  let [vatCost, inc] = calcVat(totalCost, vat);
  let totalDiscounts = calcJobsDiscounts(jobDescriptions);

  // console.log("set prices cost", setPricesCost);
  // console.log("total (exc)", totalCost);
  // console.log("vat cost", vatCost);
  // console.log("inc", inc);

  //Materials
  let materials = getMaterials(jobDescriptions);

  //Set Prices
  let setPriceList = getSetPrices(jobDescriptions);

  function formatEmailText(message) {
    return message.replace(/\n/g, "<br/>");
  }

  async function authHeaders() {
    let token = await auth.currentUser.getIdToken();
    return { Authorization: `Bearer ${token}` };
  }

  // Looks up the real bcc address for the current pdfType - only called on demand (see
  // PdfEmailBox's bcc badge tooltip), not on every render, since it's a network call.
  async function fetchBccAddress() {
    try {
      let response = await fetch(`/api/bccAddress?emailType=${pdfState.pdfType.toLowerCase()}`, {
        headers: { ...(await authHeaders()) },
      });
      if (!response.ok) return null;
      let data = await response.json();
      return data.email || null;
    } catch (err) {
      console.error("Error fetching bcc address", err);
      return null;
    }
  }

  async function sendPdf(isTest = false) {
    const apiRoute = isTest ? "/api/testEmail2" : "/api/sendEmail";

    if (pdfState.sendTo?.length === 0) {
      // No recipients
      dispatch({
        type: SET_EMAIL_SENT_STATUS,
        payload: { emailSentStatus: "No recipients", sending: false },
      });

      return;
    }

    dispatch({
      type: SET_EMAIL_SENT_STATUS,
      payload: { sending: true, emailSentStatus: "Sending..." },
    });

    try {
      const response = await fetch(apiRoute, {
        method: "POST",
        body: JSON.stringify({
          html: pdfPage.current.innerHTML,
          sendTo: pdfState.sendTo,
          attachments: pdfState.attachments,
          text: formatEmailText(pdfState.message),
          subject: pdfState.subject,
          filename: formatAsFilename(pdfState.subject),
          hesRef: hesRef,
          emailType: pdfState.pdfType.toLowerCase(),
          sendBoth: pdfState.both,
        }),
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders()),
        },
      });

      if (response.ok) {
        let dateSent = dateTimeFormatter(Date.now(), "d_full");
        dispatch({
          type: SET_EMAIL_SENT_STATUS,
          payload: { emailSentStatus: "Sent", sending: false },
        });

        if (timesheet.uid) {
          let primaryDocRef = doc(dbRef, timesheet.uid);
          let fieldUpdates = { dateSent };
          let logMessage = null;

          // Mirrors what the draft-then-confirmed-sent path already does (see
          // pendingDraftsCheck.js) - a direct Send to the customer is just as much "sent" as
          // a draft that later turned out to have gone out. "Both" means this copy is for
          // internal use (e.g. a paid+unpaid pair kept for records), not actually the
          // customer-facing send, so nothing gets auto-marked for that case - there's nothing
          // automatic to report, so no log entry either.
          if (pdfState.pdfType === "Quote") {
            fieldUpdates.status = "quote-sent";
            logMessage = "Quote sent to customer — status set to Quote Sent.";
          } else if (pdfState.pdfType === "Invoice" && !pdfState.both) {
            // An invoice going out means the job itself is finished.
            fieldUpdates.status = "completed";
            fieldUpdates.sent = true;

            if (pdfState.isPaid) {
              fieldUpdates.paid = true;
              let isCertEligible = CERT_ELIGIBLE_WORKFLOW_KEYS.has(pdfState.workflowKey);
              if (isCertEligible) fieldUpdates.cert = true;

              logMessage = `Invoice sent to customer — marked ${
                isCertEligible ? "Sent, Paid and Cert" : "Sent and Paid"
              } and status set to Completed automatically.`;
            } else {
              logMessage = "Invoice sent to customer — marked Sent and status set to Completed automatically.";
            }
          }

          if (logMessage) {
            fieldUpdates.callLog = arrayUnion({
              icon: "mail",
              note: `${AUTOMATION_PREFIX} ${logMessage}`,
              date: dateTimeFormatter(Date.now(), "d_full_variant"),
              time: dateTimeFormatter(Date.now(), "t_short"),
            });
          }

          updatePrimary(primaryDocRef, fieldUpdates).catch((err) => {
            console.error("Failure to send invoice pdf", err);
            dispatch({
              type: SET_EMAIL_SENT_STATUS,
              payload: {
                emailSentStatus: "Error updating date sent.",
                sending: false,
              },
            });
          });
        }
      } else {
        console.log("Failed to send email");
        const resData = await response.json();

        dispatch({
          type: SET_EMAIL_SENT_STATUS,
          payload: { emailSentStatus: resData.error, sending: false },
        });
      }
    } catch (err) {
      console.error("Client side err sending mail", err);

      dispatch({
        type: SET_EMAIL_SENT_STATUS,
        payload: { sending: false, emailSentStatus: "Error sending pdf" },
      });
    }
  }

  async function findQuoteThread(email) {
    if (!email) {
      setThreadCandidates([]);
      setSelectedThreadIndex(null);
      setAiSuggestedIndex(null);
      setAiSuggestedReason(null);
      setAiAttempted(false);
      setAiError(false);
      return;
    }

    try {
      const response = await fetch("/api/findQuoteThread", {
        method: "POST",
        body: JSON.stringify({
          email,
          jobDetails: timesheet.jobDetails || "",
          subject: pdfState.subject,
          aiEnabled: aiThreadMatchingEnabled,
          threadId: timesheet.threadId || "",
        }),
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders()),
        },
      });

      const data = await response.json();
      let threads = data.threads || [];

      // Backend suggestedIndex is 1-based into the candidates list; null/out-of-range -> standalone.
      let suggestedIndex =
        typeof data.suggestedIndex === "number" && data.suggestedIndex <= threads.length
          ? data.suggestedIndex - 1
          : null;

      // A thread that already has a draft sitting in it can't be selected (Gmail behaves
      // oddly with two drafts on one thread) - the badge still shows it was the AI's pick,
      // but the actual initial selection falls back to standalone rather than auto-picking
      // something the picker won't let the user attach to anyway.
      let suggestedThreadHasDraft = suggestedIndex !== null && threads[suggestedIndex]?.hasDraft;
      let initialSelectedIndex = suggestedThreadHasDraft ? null : suggestedIndex;

      setThreadCandidates(threads);
      setSelectedThreadIndex(initialSelectedIndex);
      setAiSuggestedIndex(suggestedIndex);
      setAiSuggestedReason(data.suggestedReason || null);
      setAiAttempted(Boolean(data.aiAttempted));
      setAiError(Boolean(data.aiError));
    } catch (err) {
      console.error("Error finding quote thread", err);
      setThreadCandidates([]);
      setSelectedThreadIndex(null);
      setAiSuggestedIndex(null);
      setAiSuggestedReason(null);
      setAiAttempted(false);
      setAiError(false);
    }
  }

  async function createDraft() {
    if (pdfState.sendTo?.length === 0) {
      // No recipients
      dispatch({
        type: SET_EMAIL_SENT_STATUS,
        payload: { emailSentStatus: "No recipients", sending: false },
      });

      return;
    }

    dispatch({
      type: SET_EMAIL_SENT_STATUS,
      payload: { sending: true, emailSentStatus: "Creating draft..." },
    });

    let selectedThread = selectedThreadIndex !== null ? threadCandidates[selectedThreadIndex] : null;

    // The server also sends a records copy to the invoices/quotes inbox (see sendRecordsCopy
    // in emailRoutes.js). For invoices that copy is a "BOTH" pair, so it gets the same
    // "and PAID" subject a manual Both send would have.
    let recordsSubject =
      pdfState.pdfType === "Invoice"
        ? formatSubject({
            pdfType: pdfState.pdfType,
            subjectPrefix: pdfState.subjectPrefix,
            timesheet: pdfState.timesheet,
            sendBoth: true,
          })
        : pdfState.subject;

    try {
      const response = await fetch("/api/createDraft", {
        method: "POST",
        body: JSON.stringify({
          html: pdfPage.current.innerHTML,
          sendTo: pdfState.sendTo,
          attachments: pdfState.attachments,
          text: formatEmailText(pdfState.message),
          subject: pdfState.subject,
          filename: formatAsFilename(pdfState.subject),
          hesRef: hesRef,
          emailType: pdfState.pdfType.toLowerCase(),
          sendBoth: pdfState.both,
          threadId: selectedThread?.threadId,
          threadMessageId: selectedThread?.messageId,
          threadSubject: selectedThread?.subject,
          recordsSubject,
          recordsFilename: formatAsFilename(recordsSubject),
        }),
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders()),
        },
      });

      if (response.ok) {
        const resData = await response.json();
        // Linking to the thread (rather than the single draft message) means this same
        // link keeps working once the draft is actually sent - "All Mail" resolves a
        // thread regardless of its current label, where a message-specific /drafts/ link
        // could break once the DRAFT label is gone.
        let draftUrl = resData.threadId
          ? `https://mail.google.com/mail/u/0/#all/${resData.threadId}`
          : null;

        let recordsInbox = pdfState.pdfType === "Quote" ? "quotes" : "invoices";
        let emailSentStatus =
          resData.recordsCopy?.sent === false
            ? `Draft created (copy to ${recordsInbox} inbox failed)`
            : "Draft created";

        dispatch({
          type: SET_EMAIL_SENT_STATUS,
          payload: { emailSentStatus, sending: false, draftUrl },
        });

        // Record this draft so a later visit to this record can check whether it's since
        // been sent (and auto-mark Sent/Paid/Cert) or discarded. arrayUnion is used rather
        // than a read-then-write of the current array, since `timesheet` here is a
        // navigation-state snapshot that could be stale relative to Firestore by now.
        if (timesheet.uid && resData.draftId && resData.threadId) {
          let markPaid = Boolean(pdfState.isPaid);
          let markCert = markPaid && CERT_ELIGIBLE_WORKFLOW_KEYS.has(pdfState.workflowKey);

          let pendingDraft = {
            draftId: resData.draftId,
            messageId: resData.messageId,
            threadId: resData.threadId,
            pdfType: pdfState.pdfType,
            markPaid,
            markCert,
            createdAt: Date.now(),
          };

          // Keep the primary's job type in step with whatever workflow (General/EICR/etc.)
          // was selected for this draft - mirrors the Gmail Add-on intake flow, which already
          // sets jobTypeCategory from its AI classification (see emailFieldExtractor.js).
          let jobTypeCategory = categoryForWorkflowKey(pdfState.workflowKey);

          updatePrimary(doc(dbRef, timesheet.uid), {
            pendingDrafts: arrayUnion(pendingDraft),
            hasPendingDrafts: true,
            ...(jobTypeCategory ? { jobTypeCategory } : {}),
          }).catch((err) => {
            console.error("Failed to record pending draft for later status check", err);
          });
        }
      } else {
        console.log("Failed to create draft");
        const resData = await response.json();

        dispatch({
          type: SET_EMAIL_SENT_STATUS,
          payload: { emailSentStatus: resData.error, sending: false },
        });
      }
    } catch (err) {
      console.error("Client side err creating quote draft", err);

      dispatch({
        type: SET_EMAIL_SENT_STATUS,
        payload: { sending: false, emailSentStatus: "Error creating draft" },
      });
    }
  }

  return (
    <div id="section-to-print" ref={pdfPage} className="pdfPage">
      <IdentifierBar title="PDF View" callbacks={[{ name: "PRINT", cb: printSheet }]} back={true} />
      <Footer title={"Pdf"} back={true}>
        {sliders && (
          <>
            <button
              title={tooltips.closeSlider}
              className="adder slider-control"
              onClick={() => setSliderId(null)}
            >
              <CloseIcon style={{ fontSize: 28 }} />
            </button>
            <hr />
          </>
        )}

        <button
          onClick={() => {
            dispatch({ type: TOGGLE_PDF_MENU });
          }}
          className={`adder ${pdfState.showPdfMenu && "active"}`}
        >
          <BuildIcon />
        </button>

        <button
          onClick={() => {
            dispatch({ type: TOGGLE_EMAIL_MENU });
          }}
          className={`adder ${pdfState.showEmailMenu && "active"}`}
        >
          {pdfState.sending ? <HourglassEmptyIcon /> : <MailOutlineIcon />}
        </button>
      </Footer>

      <div className="popovers">
        {pdfState.showPdfMenu && (
          <PdfToolbar
            pdf={pdfState}
            dispatch={dispatch}
            vat={vat}
            // timesheet={timesheet}
            addressLine={emailAddressLine}
            totalInvoiceCost={totalCost}
          />
        )}
        {pdfState.showEmailMenu && (
          <PdfEmailBox
            pdf={pdfState}
            dispatch={dispatch}
            timesheet={timesheet}
            totalInvoiceCost={inc}
            addressLine={emailAddressLine}
            sendPdf={sendPdf}
            createDraft={createDraft}
            findQuoteThread={findQuoteThread}
            fetchBccAddress={fetchBccAddress}
            threadCandidates={threadCandidates}
            selectedThreadIndex={selectedThreadIndex}
            onSelectThread={setSelectedThreadIndex}
            aiSuggestedIndex={aiSuggestedIndex}
            aiSuggestedReason={aiSuggestedReason}
            aiAttempted={aiAttempted}
            aiError={aiError}
            // <<<<<<<<<<<<<<<<<<<<<<
            isQuote={props.isQuote}
          />
        )}
      </div>

      <div className="pdfTimesheet">
        {pdfState.isPaid && (
          // <img className="paid-logo" src={paidStamp} alt="Paid Logo" />
          <p className="paid-logo">PAID</p>
        )}

        <div className="company">
          <div className="logo">
            <img alt="Henderson Electrical Services logo" src={hesLogo} />
          </div>
          <div className="contactInfo">
            <div className="details">
              <div className="line">
                <p className="label">web</p>
                <p className="info">hesglasgow.com</p>
              </div>
              <div className="line">
                <p className="label">email</p>
                <p className="info">hes.glasgow@gmail.com</p>
              </div>
              <div className="line">
                <p className="label">telephone</p>
                <p className="info">0141 642 0611</p>
              </div>
              <div className="line">
                <p className="label">mobile</p>
                <p className="info">07956557418</p>
              </div>
            </div>
            <div className="address">
              <div className="line">
                <p>68 MacArthur Wynd</p>
              </div>
              <div className="line">
                <p>Cambuslang</p>
              </div>
              <div className="line">
                <p>Glasgow</p>
              </div>
              <div className="line">
                <p>G72 7GA</p>
              </div>
            </div>
          </div>
        </div>
        {/* End of Comapny Logo/Details */}
        {/* Invoice Banner*/}
        <div className="typeBanner">{pdfState.pdfType.toUpperCase()}</div>

        {/* End of Invoice Banner*/}

        {/* Invoice Info And Date */}
        <div className="invoiceInfoAndDate">
          <div className="invoiceInfo">
            <p>hes {pdfState.pdfType.toLowerCase()} no:</p>
            <span>{refNumber}</span>
          </div>
          <div className="invoiceInfo">
            <p>srn:</p>
            <span>{worksOrderNumber}</span>
          </div>

          <div className="invoiceDate">
            <p>{pdfState.pdfType.toLowerCase()} date:</p>
            <span>{exportDate}</span>
          </div>
        </div>
        {/* End of Invoice Info and Date */}

        {/* Job and Client Section */}
        <div className="jobAndClientSection">
          {/* Job Details */}
          <div className="jobDetails">
            <h4>Job Details</h4>
            <div className="outlinedBox">
              <div className="line">
                <p>customer:</p>
                <span>{timesheet.name}</span>
              </div>
              <div className="line">
                <p>authorised By:</p>
                <span className="authorisedBy">{timesheet.authorisedBy}</span>
              </div>
              <div className="line">
                <p>job received:</p>
                <span>{jobReceived}</span>
              </div>
              <div className="line">
                <p>start date:</p>
                <span>{jobStart}</span>
              </div>
              <div className="line">
                <p>end date:</p>
                <span>{jobEnd}</span>
              </div>
            </div>
          </div>
          {/* End of Job Details */}
          {/* Client Details */}
          <div className="clientDetails">
            <h4>Client</h4>
            <div className="outlinedBox">
              <div className="line">
                <p>{timesheet.name}</p>
              </div>
              <div className="line">
                <p>{addressLine}</p>
              </div>
              <div className="line">{address2 ? <p>{address2}</p> : null}</div>
              <div className="line">{city ? <p>{city}</p> : null}</div>
              <div className="line">{postcode ? <p>{postcode}</p> : null}</div>
            </div>
          </div>
          {/* End of Client Details */}
        </div>
        {/* End Job and Client Section */}

        {/* Job Description */}

        {jobDescriptions?.length > 0 ? (
          <div className="jobDescription">
            <h4>Job Description</h4>
            <div className="outlinedBox">
              <p>{timesheet.jobDetails} </p>
              {jobDescriptions.map((jd) => {
                if (jd?.details?.length > 0) {
                  return (
                    <p key={jd.uid}>
                      {dateTimeFormatter(jd.jobDate, "d_full")}: {jd.details}
                    </p>
                  );
                } else return null;
              })}
            </div>
          </div>
        ) : null}

        {/* End of Job Description */}

        {/* Materials */}
        {pdfState.showMaterials && (
          <>
            {materials?.length > 0 || setPriceList?.length > 0 ? (
              <table className="materials">
                <thead>
                  <tr>
                    <th className="quantity">Quantity</th>
                    <th className="material">Material / Set Price</th>
                    <th className="cost">Cost</th>
                    <th className="total">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((mat, i) => {
                    let [price, cost] = calculateMaterialsLine(mat);

                    return (
                      <tr key={i}>
                        <td className="quantity">{mat.quantity}</td>
                        <td className="materialDescription">{mat.materialDescription}</td>
                        <td className="materialCost">{price}</td>
                        <td className="totalMaterialCost">{cost}</td>
                      </tr>
                    );
                  })}

                  {setPriceList.map((mat, i) => {
                    let [price, cost] = calculateSetPricesLine(mat);

                    return (
                      <tr style={{ background: "#f3f3f3" }} key={i}>
                        <td className="quantity">{mat.quantity}</td>
                        <td className="materialDescription">{mat.name}</td>
                        <td className="materialCost">{price}</td>
                        <td className="totalMaterialCost">{cost}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null}
          </>
        )}
        {/* End of Materials */}

        {/* Payment Section */}
        {/* Payment Info */}
        <div className="paymentSection">
          <div className="paymentInfoAndVat">
            <div className="paymentInfo">
              <span>
                Please make payments payable to: <p>Henderson Electrical Services</p>
              </span>

              <div className="bacs">
                <p>Monzo Business</p>
                <p className="accountDetails">
                  <span>Acc: 33550424</span> <span>Sort 04-00-04</span>
                </p>
              </div>
              <span>
                Please quote (Invoice <span>{refNumber}</span>) in reference box.
              </span>

              <p>THANK YOU FOR YOUR CUSTOM.</p>
            </div>
            {vat && (
              <p className="vatNumber">
                <span>VAT registration no:</span> {vat_number}
              </p>
            )}
          </div>

          {/* End of Payment Info */}

          {/* Total Costs */}
          <div className="totalCosts">
            <span className="label">Total Costs</span>
            <div className="costLines">
              {pdfState.showDiscounts && (
                <div className="costLine discounts">
                  <span className="costDesc">Discounted by</span>
                  <span className="costAmount">£{totalDiscounts}</span>
                </div>
              )}
              {pdfState.showBreakdown && (
                <>
                  <div className="costLine">
                    <span className="costDesc">{false ? `Total Materials Cost` : `Materials`}</span>
                    <span className="costAmount">£{materialsCost}</span>
                  </div>
                  <div className="costLine" style={{ background: "#f3f3f3" }}>
                    <span style={{ background: "#f3f3f3" }} className="costDesc">
                      Set Prices
                    </span>
                    <span style={{ background: "#f3f3f3" }} className="costAmount">
                      £{setPricesCost}
                    </span>
                  </div>
                  <div className="costLine">
                    <span className="costDesc">Labour</span>

                    {/* <span className="costDesc">
                  Labour: {hours} (hrs) at £{Number(ratePerHour).toFixed(2)} (x
                  {multiplier})
                </span> */}

                    {/* <!--Simple Labour Pay: for simple, remove the line (x\${props.multiplier)
      for complex, add it in just after props.tsRate
  --> */}

                    <span className="costAmount">£{labourTotal}</span>
                  </div>
                </>
              )}
              {pdfState.showVatBreakdown ? (
                <>
                  <div className="costLine">
                    <span className="costDesc">Subtotal</span>
                    <span className="costAmount">£{totalCost}</span>
                  </div>

                  <div className="costLine">
                    <span className="costDesc">VAT (20%)</span>
                    <span className="costAmount">£{vatCost}</span>
                  </div>
                </>
              ) : null}

              <div className="costLine invoiceTotalLine">
                <span className="costDesc invoiceTotalDesc">
                  {pdfState.pdfType === "Quote" ? "Total Labour & Materials" : "Invoice Total"}
                </span>
                <span className="costAmount invoiceTotalCost">£{inc}</span>
              </div>
            </div>
          </div>
        </div>
        {/* End of Total Costs */}
        {/* End of Payment Section */}
      </div>
    </div>
  );
};

export default PdfTimesheet;

// var hesLogo =
//   "data:image/png;base64,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";
